import { type ReactNode } from 'react';
import styles from './KioskStage.module.css';

export interface KioskStageProps {
  children: ReactNode;
  /**
   * Teacher-config screens (e.g. the classroom-settings page) can have
   * more content than fits one screen. In `scroll` mode the stage grows
   * with its content and scrolls vertically instead of clipping. The
   * hub and the live morning-meeting surface stay in the default fixed
   * (non-scrolling) mode.
   */
  scroll?: boolean;
  /**
   * Live morning-meeting surface. Reserves the fuller safe area so page
   * content clears the fixed edge chrome (back button, side chevrons,
   * bottom nav / clock / fullscreen). The hub uses the lighter edge pad.
   */
  live?: boolean;
}

/**
 * Viewport-filling wrapper. Children render directly into the real
 * browser viewport and lay out fluidly off the global --ui-scale knob,
 * so the same layout works on a 720p projector, a 1080p TV, and a 4K
 * board without a fixed design canvas. See KioskStage.module.css.
 */
export function KioskStage({ children, scroll = false, live = false }: KioskStageProps) {
  if (scroll) {
    return (
      <div className={styles.scrollViewport}>
        <div className={styles.scrollStage}>{children}</div>
      </div>
    );
  }
  const stageClass = live ? `${styles.stage} ${styles.stageLive}` : styles.stage;
  return (
    <div className={styles.viewport}>
      <div className={stageClass}>{children}</div>
    </div>
  );
}
